import {
  DakenService,
  LogService,
  MySquid,
  onConfigChange,
  onProfileCompletenessChange,
  onProfilePercentageChange,
  ProfileCompleteness,
  User,
  UserHelper,
  LanguageDropdown,
  defaultUser,
} from '@hub/shared'
import { menuItem } from '@layouts/default/default.layout'
import { SqCircleCompletation, SqDataLayerHelper, useSqScopedCssHash } from '@squidit/react-css'
import onModalCreatorsInsightsObservable from '@src/observables/on-modal-creators-insights.observable'
import React, { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SubscriptionLike } from 'rxjs'
import './user-menu-default.component.scoped.scss'
import { showInsights } from '@src/utils/showInsights'
import { UserMenuItem } from './components/user-menu-item/user-menu-item-default.component'
import { useMediaQuery } from '@src/hooks/useMediaQuery'
import { Md5 } from 'ts-md5/dist/md5'
interface Props extends React.HTMLAttributes<HTMLDivElement> {
  configs: MySquid | null
  user: User | null
}

export const UserMenu = ({ className = '', configs, user }: Props) => {
  const [userCompleteness, setUserCompleteness] = useState<boolean>(true)
  const onProfileCompletenessChangeSubscription = useRef<SubscriptionLike | undefined>(undefined)
  const userMenuRef = useRef<any>(null)
  const menuButton = useRef<HTMLButtonElement>(null)
  const [userPercentage, setUserPercentage] = useState<number>(0)
  const logService = useMemo(() => new LogService(), [])
  const dakenService = useMemo(() => new DakenService(), [])
  const userHelper = useMemo(() => new UserHelper(), [])
  const [config, setConfig] = useState<MySquid | null>(null)
  const { t } = useTranslation('defaultLayout')
  logService.init('user Menu Default Component')
  const hash = useSqScopedCssHash(userMenuRef)
  const isSmallScreen = useMediaQuery('(max-width: 991px)')
  const [openUserMenu, setOpenUserMenu] = useState<boolean>(false)

  const [userMenu, setUserMenu] = useState<menuItem[]>([
    {
      label: 'userMenu.profile',
      path: '/profile',
      icon: 'address-card',
    },
    {
      label: 'userMenu.research',
      path: '/profile/progressive',
      icon: 'poll-people',
    },
    {
      label: 'userMenu.mediakit',
      path: '/mediakit',
      icon: 'photo-film',
    },
    {
      label: 'userMenu.socialNetworks',
      path: '/profile/social-networks',
      icon: 'link',
    },
    {
      label: 'userMenu.payments',
      path: '/payments/data',
      icon: 'money-check-dollar-pen',
    },
    {
      label: 'userMenu.help',
      path: '/help',
      icon: 'circle-question',
    },
  ])

  const generatePicture = useCallback((email = '', name = '') => {
    const gravatarLink = 'https://www.gravatar.com/avatar/'
    const md5email = Md5?.hashStr(email)
    const uiAvatarLink = `https://ui-avatars.com/api/${name?.split(' ').join('+')}/128/random`
    return `${gravatarLink}${md5email}?d=${uiAvatarLink}`
  }, [])

  const getUserPic = useCallback(() => {
    return user?.picture || user?.auth0pic || generatePicture(user?.profileadditionalinfo?.email, user?.profileadditionalinfo?.name)
  }, [user, generatePicture])

  const handleButtonCreatorsInsightsClick = useCallback(async () => {
    onModalCreatorsInsightsObservable.next(true)
  }, [])

  useEffect(() => {
    const onConfigChangeObservable: SubscriptionLike = onConfigChange.subscribe((newConfig: MySquid | any) => {
      setConfig(newConfig)
    })

    return () => {
      onConfigChangeObservable?.unsubscribe()
    }
  }, [])

  useEffect(() => {
    if (openUserMenu && isSmallScreen) {
      document.body.classList.add('no-scroll')
    } else {
      document.body.classList.remove('no-scroll')
    }
  }, [openUserMenu])

  useEffect(() => {
    if (showInsights({ configs, user })) {
      const hasCreatorsInsightsItem = userMenu.find((it) => it.label === 'userMenu.creatorsInsights')

      if (!hasCreatorsInsightsItem) {
        const item: menuItem = {
          label: 'userMenu.creatorsInsights',
          icon: 'chart-user',
          onClick: () => null,
          isNew: true,
        }
        item.onClick = () => handleButtonCreatorsInsightsClick()
        setUserMenu([...userMenu, item])
      }
    }
  }, [config, user, userMenu, handleButtonCreatorsInsightsClick])

  const logout = (e: any) => {
    e.preventDefault()
    userHelper.deleteUser()
    setOpenUserMenu(false)
    setTimeout(() => {
      history.pushState({}, '', '/login')
    }, 500)
  }

  const parseCompleteness = (menuItem: menuItem) => {
    switch (menuItem.label) {
      case 'userMenu.profile':
        return 'registration'
      case 'userMenu.research':
        return 'progressive'
      case 'userMenu.mediakit':
        return 'mediakitCount'
      case 'userMenu.socialNetworks':
        return 'socialNetwork'
      case 'userMenu.payments':
        return 'paymentData'
      default:
        return ''
    }
  }

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target as Node) &&
        !menuButton.current?.contains(event.target as Node)
      ) {
        setOpenUserMenu(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [setOpenUserMenu, userMenuRef])

  useEffect(() => {
    if (user?.squidId) {
      dakenService
        .getInflucencersCompleteness(user?.squidId)
        .then((completeness: ProfileCompleteness | any) => {
          onProfileCompletenessChange.next(completeness)
          onProfilePercentageChange.next(completeness?.registration?.percentage)
          setUserPercentage(completeness?.percentage)
          setUserCompleteness(completeness?.percentage >= 100 || !completeness?.percentage)
          setUserMenu(
            userMenu
              .filter((it) => it.label !== 'userMenu.mediakit' || !configs?._id)
              ?.map((item) => {
                const key = parseCompleteness(item)
                return {
                  ...item,
                  warning: completeness[key]?.percentage < 100,
                }
              })
          )
        })
        .catch((error) => {
          logService.error({
            message: 'Error getting completeness',
            error,
          })
        })
    }
  }, [user, configs, dakenService, logService])

  useEffect(() => {
    onProfileCompletenessChangeSubscription.current = onProfileCompletenessChange.subscribe((completeness: ProfileCompleteness) => {
      setUserPercentage(completeness?.percentage)
      setUserCompleteness(completeness?.percentage >= 100 || !completeness?.percentage)
    })

    return () => onProfileCompletenessChangeSubscription.current?.unsubscribe()
  }, [])

  return (
    <>
      <button
        className={`item user-profile-wrap ${openUserMenu ? 'active' : ''}`}
        onClick={() => {
          setOpenUserMenu(!openUserMenu)
        }}
        ref={menuButton}
      >
        <span className={`item user-profile ${openUserMenu ? 'active' : ''}`}>
          <button id="user-menu-default-button" type="button">
            <figure className="menu-photo">
              <img
                src={getUserPic()}
                alt={user?.profileadditionalinfo?.name}
                title={user?.profileadditionalinfo?.name}
                className="avatar"
                onError={({ currentTarget }) => {
                  currentTarget.onerror = null
                  currentTarget.src = defaultUser
                }}
              />
            </figure>
          </button>
        </span>
        <i className={`fa-solid fa-chevron-down ${openUserMenu ? 'up' : ''}`}></i>
      </button>
      <nav className={`user-menu scrollbar ${openUserMenu ? 'open' : ''} ${className}`} ref={userMenuRef}>
        <div className="user-menu-wrapper">
          <div className="card-name-with-close-wrapper">
            <div className={'card-name-wrapper'}>
              <SqCircleCompletation
                value={userPercentage}
                image={user?.picture || user?.auth0pic}
                size="small"
                externalCssScopedHash={hash}
                className="user-circle-completition"
              />
              <div className="card-name-user-info">
                <span className="card-name-social-name">
                  {user?.profileadditionalinfo?.socialName || user?.profileadditionalinfo?.name}
                </span>
                <span className="card-name-email">{user?.profileadditionalinfo.email ?? ''}</span>
              </div>
            </div>
            <button onClick={() => setOpenUserMenu(false)}>
              <i className={'fa-solid fa-close'} />
            </button>
          </div>
          {/* {!userCompleteness && (
          <div className="user-completeness">
            <div className="user-completenes-text-wrapper">
              <span className="user-completeness-title">{t('userMenu.userCompletenessTitle')}</span>
              <span className="user-completeness-warning">3 de 6 completos</span>
            </div>
            <i className="fa-solid fa-chevron-right"></i>
          </div>
        )} */}
          <div className="menu-items-wrapper">
            <UserMenuItem link="/profile/data" title={t('userMenu.profile')} iconClasses="fa-id-card" setOpen={setOpenUserMenu} />
            <UserMenuItem
              link="/profile/progressive"
              title={t('userMenu.research')}
              iconClasses="fa-poll-people"
              setOpen={setOpenUserMenu}
            />
            <UserMenuItem
              link="/payments/data"
              title={t('userMenu.payments')}
              iconClasses="fa-money-check-dollar-pen"
              setOpen={setOpenUserMenu}
            />
            <UserMenuItem
              link="/profile/notifications"
              title={t('userMenu.manageNotifications')}
              iconClasses="fa-bell"
              setOpen={setOpenUserMenu}
            />
            <UserMenuItem
              onClick={(e) => {
                if (e.target instanceof HTMLElement && e.target.classList.contains('checkbox')) {
                  return
                } else {
                  const childButton = document.querySelector<HTMLButtonElement>('.dark-mode .wrapper-selectors label')
                  childButton?.click()
                }
              }}
              title={t('userMenu.darkMode')}
              iconClasses="fa-moon"
              setOpen={setOpenUserMenu}
            />
            <UserMenuItem onClick={logout} title={t('userMenu.logout')} iconClasses="fa-right-from-bracket" setOpen={setOpenUserMenu} />
            {isSmallScreen && <LanguageDropdown />}
          </div>
        </div>
      </nav>
    </>
  )
}
